import { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { styled } from '@mui/system';
import Navbar from './Navbar';
import Sidebar from './Sidebar';
import { Box, Typography } from '@mui/material';

const APP_BAR_MOBILE = 64;
const APP_BAR_DESKTOP = 92;

const RootStyle = styled('div')({
  // marginTop: '200px !important',
  display: 'flex',
  minHeight: '100dvh', // dynamic viewport for iOS
  overflow: 'hidden',
  pt: 0,
});

const MainStyle = styled('div')(({ theme }) => ({
  flexGrow: 1,
  overflow: 'auto',
  minHeight: '100%',
  zIndex: 10,
  [theme.breakpoints.up('lg')]: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
}));

// Robust mobile/tablet detection (covers iPadOS desktop UA)
const isMobileOrTabletDevice = () => {
  if (typeof window === 'undefined') return false;
  const userAgent = typeof window.navigator === "undefined" ? "" : navigator.userAgent;
  const isIPadDesktopUA =
    navigator.platform === 'MacIntel' && (navigator as any).maxTouchPoints > 1;
  return Boolean(userAgent.match(
    /Android|BlackBerry|iPhone|iPad|iPod|Opera Mini|IEMobile|WPDesktop/i
  )) || isIPadDesktopUA;
};

const isTabletDevice = () => {
  if (typeof window === 'undefined') return false;
  const userAgent = typeof window.navigator === "undefined" ? "" : navigator.userAgent;
  const iPadDesktopUA =
    navigator.platform === 'MacIntel' && (navigator as any).maxTouchPoints > 1;

  // Touch Windows laptops report maxTouchPoints too, so rely on mobile/tablet UA here.
  const tabletUA = Boolean(userAgent.match(/iPad|Android/i)) || iPadDesktopUA;
  const width = window.innerWidth;
  return tabletUA && width >= 768 && width <= 1366;
};


export default function Layout() {
  const [open, setOpen] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(
    localStorage.getItem('isHide') === 'true' ? 95 : 300
  );
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const handleSidebarWidthChange = (value: number) => {
    setSidebarWidth(value);
  };

  // Track width + orientation (via media query)
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 0
  );
  const [isLandscape, setIsLandscape] = useState(
    typeof window !== 'undefined' &&
      window.matchMedia('(orientation: landscape)').matches
  );

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    const mq = window.matchMedia('(orientation: landscape)');
    const onOrientation = () => setIsLandscape(mq.matches);

    window.addEventListener('resize', handleResize);
    mq.addEventListener('change', onOrientation);

    // in case we mounted after a rotate/rescale
    handleResize();
    onOrientation();

    return () => {
      window.removeEventListener('resize', handleResize);
      mq.removeEventListener('change', onOrientation);
    };
  }, []);

  const isVideoPath = pathname.includes('/video');
  const mobileOrTablet = isMobileOrTabletDevice();

  // === Core rule:
  // Tablets behave like desktop always (both orientations, all pages)
  // Phones act like desktop only on /video in landscape
  const isTablet = isTabletDevice();

  const showDesktopChrome =
    !mobileOrTablet || isTablet || (isVideoPath && mobileOrTablet && isLandscape);

  // paddingTop logic:
  let paddingTopValue = APP_BAR_MOBILE + 24; // default mobile spacing (64 + 24 = 88)
  if (showDesktopChrome) {
    paddingTopValue = APP_BAR_DESKTOP + 24; // 92 + 24 = 116
  }


  return (
    <RootStyle>
      <Navbar sidebarWidth={sidebarWidth} />
      {/* Sidebar handles its own “persist on /video landscape mobile” logic (from Sidebar.tsx changes) */}
      <Sidebar onWidthChange={handleSidebarWidthChange} />

      <MainStyle
        id="main-style"
        sx={{
          paddingTop: `${paddingTopValue}px`,
          '@media (min-width:1200px)': {
            paddingTop: `${APP_BAR_DESKTOP + 24}px`,
          },
        }}
      >
        <Outlet />
      </MainStyle>
    </RootStyle>
  );
}
